import { useState, useCallback } from 'react';
import { Share2, Linkedin, Twitter, Instagram, Copy, Check } from 'lucide-react';

const WEBSITE_URL = 'https://www.aaimpactinc.com';

function buildShareText(platform, event, session, comparison) {
  const country = event?.country || 'Unknown';
  const gpName = event?.grand_prix || event?.event_name || 'Grand Prix';
  const sessionLabel = session === 'sprint_qualifying' ? 'Sprint Qualifying' : session === 'qualifying' ? 'Qualifying' : session === 'sprint' ? 'Sprint' : 'Race';
  const topDriver = comparison?.f1_results?.results?.[0];
  const topEsg = comparison?.sustainability_leaders?.[0];
  const esgCount = comparison?.sustainability_leaders?.length || 0;

  const driverLine = topDriver ? `P1: ${topDriver.driver_name} (${topDriver.team_name})` : '';
  const esgLine = topEsg ? `Top ESG: ${topEsg.name} (${topEsg.overall_score}/100)` : '';

  if (platform === 'linkedin') {
    return [
      `${gpName} ${sessionLabel} - F1 x Sustainability Update`,
      '',
      driverLine,
      esgLine ? `${esgLine} - leading ${country}'s sustainability scorecard` : '',
      esgCount ? `${esgCount} companies tracked in ${country}` : '',
      '',
      `Explore the full analysis on our live dashboard:`,
      WEBSITE_URL,
      '',
      '#F1 #Sustainability #ESG #AAImpact #Formula1',
    ].filter(Boolean).join('\n');
  }

  if (platform === 'twitter') {
    return [
      `${gpName} ${sessionLabel} update`,
      '',
      driverLine,
      esgLine,
      '',
      `Full dashboard: ${WEBSITE_URL}`,
      '',
      '#F1 #ESG #Sustainability',
    ].filter(Boolean).join('\n');
  }

  if (platform === 'instagram') {
    return [
      `${gpName} ${sessionLabel} - F1 x Sustainability`,
      '',
      driverLine,
      esgLine ? `${esgLine} leads ${country}'s ESG scorecard` : '',
      '',
      `See the full live dashboard - link in bio`,
      WEBSITE_URL,
      '',
      '#F1 #Sustainability #ESG #DataDriven #AAImpact #Formula1 #CleanEnergy #CorporateResponsibility',
    ].filter(Boolean).join('\n');
  }

  return '';
}

export default function ShareScorecard({ event, session, comparison }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(null);

  const handleCopy = useCallback(async (platform) => {
    const text = buildShareText(platform, event, session, comparison);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(platform);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(platform);
      setTimeout(() => setCopied(null), 2000);
    }
  }, [event, session, comparison]);

  const platforms = [
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
    { id: 'twitter', label: 'X / Twitter', icon: Twitter, color: '#1DA1F2' },
    { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F' },
  ];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`share-scorecard-btn-${session}`}
        className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white"
      >
        <Share2 className="h-4 w-4" />
        Share
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 w-52 rounded-2xl border border-white/10 bg-[#0D2238] p-2 shadow-[0_12px_40px_rgba(0,0,0,0.5)]" data-testid="share-dropdown">
            <p className="mb-2 px-2 text-[10px] uppercase tracking-[0.16em] text-white/30">Copy for platform</p>
            {platforms.map((p) => {
              const Icon = p.icon;
              const isCopied = copied === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => handleCopy(p.id)}
                  data-testid={`share-copy-${p.id}`}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white"
                >
                  <Icon className="h-4 w-4" style={{ color: p.color }} />
                  <span className="flex-1">{p.label}</span>
                  {isCopied ? (
                    <Check className="h-4 w-4 text-[#2A9D8F]" />
                  ) : (
                    <Copy className="h-4 w-4 text-white/25" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
